import lod_ from "lodash";
import { useDispatch } from "react-redux";
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Icon,
	IconButton,
	Tooltip,
	Checkbox,
	CircularProgress
} from "@mui/material";
import MDBox from "components/Basics/MDBox";
import MDButton from "components/Basics/MDButton";
import MDTypography from "components/Basics/MDTypography";
import KnowledgeActions from "redux-react/actions/knowledgeActions";
import MergeKnowledgeDnd from "./MergeKnowledgeDnd";
import { useEffect, useState } from "react";
import TextDetails from "../TextDetails";
import { display } from "redux-react/reducers/snackBarReducer";
import { t } from "i18next";

/**
 * Merge knowledge component
 * Allows merging a knowledge with other selected knowledges.
 * @param {Object} props - Component properties
 * @param {Object} props.knowledge - The knowledge to merge with others.
 * @param {Array} props.knowledges - List of all knowledges to select from.
 * @param {Function} props.reload - Function to reload the knowledges after merging.
 * @returns {JSX.Element} The rendered component.
 */
const MergeKnowledge = ({ knowledge, knowledges, reload = () => {} }) => {
	const dispatch = useDispatch();
	const [loading, setLoading] = useState(false);

	const [selectedKnowledgesUUID, setSelectedKnowledgesUUID] = useState([]);
	const [openDialog, setOpenDialog] = useState(false);

	/**
	 * Check if the save button should be disabled
	 * @returns {void}
	 */
	const isDisabled = () => {
		if (loading) {
			return true;
		}

		return false;
	};

	/**
	 * Handle save action
	 * @returns {void}
	 */
	const onSave = () => {
		if (lod_.isNil(knowledge.uuid)) {
			return;
		}

		setLoading(true);

		const displayError = () => {
			setLoading(false);
			dispatch(
				display({
					type: "error",
					message: t("FAIQ.ERROR.errorWhileUpdateKnowledge")
				})
			);
		};

		const onSuccess = () => {
			dispatch(
				display({
					type: "success",
					message: t("FAIQ.SUCCESS.knowledgeUpdated")
				})
			);
			setLoading(false);
			setOpenDialog(false);
			reload();
		};

		dispatch(
			KnowledgeActions.mergeKnowledges(
				selectedKnowledgesUUID,
				() => onSuccess(),
				() => displayError()
			)
		);
	};

	useEffect(() => {
		setSelectedKnowledgesUUID([knowledge.uuid]);
	}, [knowledge]);

	return (
		<MDBox>
			{/* Button */}
			<Tooltip title={t("SETTINGS.merge")} placement="top">
				<IconButton size="small" onClick={() => setOpenDialog(true)}>
					<Icon>merge</Icon>
				</IconButton>
			</Tooltip>
			{/* Dialog */}
			<Dialog
				fullWidth
				open={openDialog}
				PaperProps={{
					sx: {
						height: "95%",
						width: "100%",
						maxWidth: "100%"
					}
				}}
			>
				<DialogTitle>
					<MDTypography variant="h5" fontWeight="bold">
						{t("FAIQ.ACTIONS.mergeKnowledgesTitle")}
					</MDTypography>
					<MDTypography variant="body2" color="textSecondary">
						{t("FAIQ.ACTIONS.mergeKnowledgesSubtitle")}
					</MDTypography>
				</DialogTitle>
				<DialogContent
					sx={{ display: "flex", flexDirection: "row", height: "100%", overflow: "hidden" }}
				>
					{/* Check */}
					<MDBox flex="4" display="flex" flexDirection="column">
						{/*  */}
						<MDTypography variant="h6" fontSize="small">
							{t("SETTINGS.merge")}
						</MDTypography>
						<TextDetails
							text={knowledge?.content || t("FAIQ.ACTIONS.noContent")}
							minHeight="10vh"
							fontSize="0.8rem"
						/>
						<MDTypography variant="h6" fontSize="small">
							{t("TEXT.with")}
						</MDTypography>
						{/*  */}
						<MDBox style={{ overflowY: "auto" }}>
							{knowledges.map((k, index) => {
								if (k.uuid === knowledge.uuid) {
									// Skip the current knowledge
									return null;
								}
								return (
									<MDBox
										key={index}
										mb={1}
										p={1}
										borderRadius="md"
										display="flex"
										alignItems="center"
										onClick={() => {
											if (loading) {
												return;
											}
											setSelectedKnowledgesUUID(prev => {
												if (prev.includes(k.uuid)) {
													// If already selected, remove it
													return prev.filter(uuid => uuid !== k.uuid);
												} else {
													// If not selected, add it
													return [...prev, k.uuid];
												}
											});
										}}
										bgColor={selectedKnowledgesUUID.includes(k.uuid) ? "light" : "white"}
										variant="gradient"
										style={{
											cursor: "pointer"
										}}
									>
										<Checkbox checked={selectedKnowledgesUUID.includes(k.uuid)} />
										<MDBox flex={1} ml={1}>
											<TextDetails
												text={k.content || t("FAIQ.ACTIONS.noContent")}
												minHeight="10vh"
												fontSize="0.8rem"
											/>
										</MDBox>
									</MDBox>
								);
							})}
						</MDBox>
					</MDBox>
					{/* D&D */}
					<MDBox flex="2" ml={2} display="flex" flexDirection="column">
						<MDTypography variant="h6" fontSize="small">
							{t("FAIQ.ACTIONS.rerankMergeTitle")}
						</MDTypography>
						<MDTypography variant="body2" fontSize="small">
							{t("FAIQ.ACTIONS.rerankMergeSubtitle")}
						</MDTypography>
						<MDBox style={{ overflowY: "auto" }}>
							<MergeKnowledgeDnd
								items={selectedKnowledgesUUID}
								onChange={newItems => {
									setSelectedKnowledgesUUID(newItems);
								}}
								knowledges={knowledges}
							/>
						</MDBox>
					</MDBox>
				</DialogContent>
				<DialogActions>
					<MDButton
						variant="outlined"
						color="info"
						onClick={() => setOpenDialog(false)}
						disabled={loading}
					>
						{t("SETTINGS.cancel")}
					</MDButton>
					{!loading && (
						<MDButton variant="contained" color="info" onClick={onSave} disabled={isDisabled()}>
							{t("SETTINGS.save")}
						</MDButton>
					)}
					{loading && (
						<MDButton variant="contained" color="info" disabled>
							<CircularProgress size={24} color="white" />
							&nbsp;{t("SETTINGS.saving")}
						</MDButton>
					)}
				</DialogActions>
			</Dialog>
		</MDBox>
	);
};

export default MergeKnowledge;
